import type { Dict } from "../types";

export const marketing: Dict = {
  en: {

    // Marketing module
    "Marketing.title": "Marketing",
    "Marketing.composer.title": "New email campaign",
    "Marketing.composer.description": "Email the clients who have an appointment in the selected date range.",
    "Marketing.composer.descriptionWinback": "Send an email to clients who have not come back within the number of days you choose.",
    "Marketing.composer.descriptionSingleVisit": "Send an email to clients who came once and have no upcoming appointment.",
    "Marketing.composer.descriptionNewClients": "Send an email to clients whose file was created in the date range you choose.",
    "Marketing.composer.descriptionLoyal": "Send an email to clients who reached the number of visits you choose.",
    "Marketing.type.label": "Email type",
    "Marketing.type.operational": "Operational",
    "Marketing.type.marketing": "Promotional",
    "Marketing.type.operationalHelper": "Service notice sent to every client in the range (e.g. closure, schedule change).",
    "Marketing.type.marketingHelper": "Promotion: excludes unsubscribed clients and adds an unsubscribe link.",
    "Marketing.range.label": "Date range",
    "Marketing.range.day": "Day",
    "Marketing.range.week": "Week",
    "Marketing.range.month": "Month",
    "Marketing.range.custom": "Custom",
    "Marketing.range.start": "Start date",
    "Marketing.range.end": "End date",
    "Marketing.subject.label": "Subject",
    "Marketing.subject.placeholder": "Email subject",
    "Marketing.body.label": "Message",
    "Marketing.body.placeholder": "Write your message…",
    "Marketing.charCount": "{count} / {max}",
    "Marketing.preview.loading": "Counting recipients…",
    "Marketing.preview.count": "{count} clients will be contacted",
    "Marketing.preview.countOne": "{count} client will be contacted",
    // "excludedOne" is intentionally identical to the plural form: English
    // doesn't inflect this phrase by count (only the {count} value changes).
    // Same for "Marketing.confirm.excludedOne" and "Marketing.confirm.unreachableOne"
    // below. Don't "fix" these into a different string during a translation pass.
    "Marketing.preview.excluded": "({count} excluded as unsubscribed)",
    "Marketing.preview.excludedOne": "({count} excluded as unsubscribed)",
    "Marketing.preview.unreachable": "Cannot be reached: {count}. Without a client file, no unsubscribe link can be provided, which the law requires for a promotional email. Create the file to include them.",
    "Marketing.confirm.unreachable": " · {count} cannot be reached",
    "Marketing.confirm.unreachableOne": " · {count} cannot be reached",
    "Marketing.confirm.excluded": " · {count} unsubscribed",
    "Marketing.confirm.excludedOne": " · {count} unsubscribed",
    "Marketing.preview.none": "No client matches this audience",
    "Marketing.preview.noneRange": "No client in this range",
    "Marketing.preview.error": "Unable to count recipients",
    "Marketing.preview.viewRecipients": "View recipients",
    "Marketing.preview.hideRecipients": "Hide recipients",
    "Marketing.preview.viewUnreachable": "View the {count} people who cannot be reached",
    "Marketing.preview.viewUnreachableOne": "View the person who cannot be reached",
    "Marketing.preview.hideUnreachable": "Hide the people who cannot be reached",
    "Marketing.preview.recipientsLoading": "Loading recipients…",
    "Marketing.preview.recipientsError": "Unable to load recipients",
    "Marketing.preview.recipientsTruncated": "Showing the first 1,000 recipients out of {total}.",
    "Marketing.preview.listTruncated": "Only the first 1,000 are shown.",
    "Marketing.send.when": "When",
    "Marketing.send.now": "Now",
    "Marketing.send.schedule": "Schedule",
    "Marketing.send.scheduledFor": "Scheduled for",
    "Marketing.send.scheduledHelper": "The send time must be in the future.",
    "Marketing.send.submit": "Send",
    "Marketing.send.submitScheduled": "Schedule",
    "Marketing.send.sending": "Sending…",
    "Marketing.send.test": "Send a test to myself",
    "Marketing.send.testSending": "Sending test…",
    "Marketing.send.testHelper": "The test will be sent to {email}",
    "Marketing.toast.created": "Campaign created, {count} recipients",
    "Marketing.toast.scheduled": "Campaign scheduled, {count} recipients",
    "Marketing.toast.noRecipients": "No client in this range",
    "Marketing.toast.testSent": "Test email sent",
    "Marketing.toast.createFailed": "Could not create the campaign",
    "Marketing.toast.testFailed": "Could not send the test email",
    "Marketing.toast.missingAddress": "Add your salon’s address before sending a campaign. Canada’s anti-spam law requires a mailing address in every promotional email.",
    "Marketing.limit.remaining": "{used} / {max} campaigns this month",
    "Marketing.limit.reached": "Monthly limit reached ({limit} campaigns/month)",
    "Marketing.validation.subjectRequired": "A subject is required",
    "Marketing.validation.bodyRequired": "A message is required",
    "Marketing.validation.rangeInvalid": "The start date must be before the end date",
    "Marketing.validation.scheduleInPast": "The scheduled time must be in the future",
    "Marketing.validation.previewPending": "Recipients are still being calculated, try again in a moment",
    "Marketing.history.title": "Campaign history",
    "Marketing.history.empty": "No campaign yet",
    "Marketing.history.loading": "Loading campaigns…",
    "Marketing.history.recipients": "{count} recipients",
    "Marketing.history.results": "{sent} sent / {failed} failed",
    "Marketing.history.scheduledFor": "Scheduled for {date}",
    "Marketing.history.range": "{start} → {end}",
    "Marketing.history.cancel": "Cancel",
    "Marketing.history.cancelConfirmAction": "Cancel the campaign",
    "Marketing.history.cancelKeep": "Keep the campaign",
    "Marketing.history.cancelling": "Cancelling…",
    "Marketing.history.cancelConfirm": "Cancel this scheduled campaign?",
    "Marketing.history.cancelConfirmDescription": "It will not be sent. This cannot be undone.",
    "Marketing.history.cancelTitle": "Cancel campaign",
    "Marketing.history.cancelSuccess": "Campaign cancelled",
    "Marketing.history.cancelFailed": "Could not cancel the campaign",
    "Marketing.status.draft": "Draft",
    "Marketing.status.scheduled": "Scheduled",
    "Marketing.status.sending": "Sending",
    "Marketing.status.sent": "Sent",
    "Marketing.status.failed": "Failed",
    "Marketing.status.cancelled": "Cancelled",
    "Marketing.tokens.legend": "These fields will be replaced with each client's info when the email is sent.",
    "Marketing.tokens.clientGroup": "Client",
    "Marketing.tokens.salonGroup": "Salon",
    "Marketing.tokens.nom_complet": "Full name",
    "Marketing.tokens.prenom": "First name",
    "Marketing.tokens.courriel_client": "Email",
    "Marketing.tokens.telephone_client": "Phone",
    "Marketing.tokens.salon": "Salon name",
    "Marketing.tokens.adresse": "Address",
    "Marketing.tokens.telephone_salon": "Phone",
    "Marketing.tokens.courriel_salon": "Email",
    "Marketing.tokens.site_web": "Website",
    "Marketing.tokens.tooLong": "This field is limited to {max} characters, the token does not fit.",
    "Marketing.draft.reset": "Reset",
    "Marketing.draft.restored": "Draft restored",
    "Marketing.resetConfirmTitle": "Reset the campaign?",
    "Marketing.resetConfirmBody": "The subject, the message and every setting will go back to their default values. This cannot be undone.",
    "Marketing.resetConfirmAction": "Reset",
    "Marketing.previewEmail.button": "Preview",
    "Marketing.previewEmail.loading": "Generating preview…",
    "Marketing.previewEmail.titlePrefix": "Preview",
    "Marketing.previewEmail.subjectLabel": "Subject:",
    "Marketing.previewEmail.error": "Could not generate the preview",
    "Marketing.confirm.title": "Send this campaign?",
    "Marketing.confirm.body": "{count} recipients · {type} · {range} · {when}",
    "Marketing.confirm.bodyOne": "{count} recipient · {type} · {range} · {when}",
    "Marketing.confirm.typeOperational": "Operational",
    "Marketing.confirm.typeMarketing": "Promotional",
    "Marketing.confirm.whenNow": "sent now",
    "Marketing.confirm.whenScheduled": "scheduled for {date}",
    "Marketing.confirm.confirm": "Send",
    "Marketing.confirm.confirmScheduled": "Schedule",
    "Marketing.confirm.cancel": "Cancel",
    "Marketing.history.duplicate": "Duplicate",

    // Marketing — unified header
    "Marketing.headerSubtitle": "Email campaigns to your clients",
    "Marketing.tileCampaignsThisMonth": "Campaigns this month",
    "Marketing.tileRecipients": "Targeted recipients",
    "Marketing.tileScheduled": "Scheduled",
    "Marketing.tileLastSend": "Last send",
    "Marketing.tileNeverSent": "Never",
    "Marketing.tabCompose": "Compose",
    "Marketing.tabHistory": "History",
    "Marketing.tabBrand": "Brand",
    "Marketing.brand.title": "Your brand identity",
    "Marketing.brand.description": "Set this up once. Every campaign email will use it automatically.",
    "Marketing.brand.logo": "Logo",
    "Marketing.brand.logoHelper": "Shown at the top of every campaign email. Defaults to your salon logo.",
    "Marketing.brand.banner": "Banner image",
    "Marketing.brand.bannerHelper": "Optional wide image above the logo. Turn it on per campaign.",
    "Marketing.brand.upload": "Upload an image",
    "Marketing.brand.remove": "Remove",
    "Marketing.brand.usingSalonLogo": "Using your salon logo",
    "Marketing.brand.accent": "Accent color",
    "Marketing.brand.accentAuto": "Automatic",
    "Marketing.brand.accentHelper": "Used for headings and the action button.",
    "Marketing.brand.ctaLabel": "Button label",
    "Marketing.brand.ctaLabelPlaceholder": "Book an appointment",
    "Marketing.brand.ctaUrl": "Button link",
    "Marketing.brand.ctaUrlInvalid": "Enter a link starting with http:// or https://.",
    "Marketing.brand.useBookingLink": "Use my booking page",
    "Marketing.brand.instagram": "Instagram",
    "Marketing.brand.facebook": "Facebook",
    "Marketing.brand.signature": "Signature",
    "Marketing.brand.signaturePlaceholder": "The team at …",
    "Marketing.brand.contactNote": "Your address, phone and website come from Salon information and appear in the email footer.",
    "Marketing.brand.save": "Save brand identity",
    "Marketing.brand.saved": "Brand identity saved",
    "Marketing.brand.oldImageNotRemoved": "Your images are saved, but an old file could not be removed.",
    "Marketing.brand.saveFailed": "Could not save the brand identity",
    "Marketing.brand.loadFailed": "Could not load the brand identity. Please try again later. Saving now would erase what is already configured.",
    "Marketing.brand.uploadFailed": "Could not upload the image",
    "Marketing.brand.imageTooLarge": "Image must be 5 MB or less",
    "Marketing.brand.imageInvalidType": "Use a JPEG, PNG or WebP image",
    "Marketing.brand.unsavedImage": "Preview not saved yet. Click Save to keep it.",
    "Marketing.blocks.title": "Layout (optional)",
    "Marketing.blocks.heading": "Headline",
    "Marketing.blocks.headingPlaceholder": "Summer promo",
    "Marketing.blocks.image": "Image",
    "Marketing.blocks.imageUpload": "Upload an image",
    "Marketing.blocks.imageRemove": "Remove image",
    "Marketing.blocks.showBanner": "Show my banner",
    "Marketing.blocks.showCta": "Show an action button",
    "Marketing.blocks.ctaLabelOverride": "Button label (overrides your brand)",
    "Marketing.blocks.ctaUrlOverride": "Button link (overrides your brand)",
    "Marketing.blocks.brandHint": "Blank fields fall back to your brand identity.",
    "Marketing.blocks.bannerMissing": "No banner in your brand identity, so nothing will show. Add one in the Brand tab.",
    "Marketing.blocks.ctaMissing": "No button link, here or in your brand identity, so no button will show.",
    // Problème 166, partie B : choosing a layout image no longer uploads it
    // immediately. This note explains why the preview shown is a local one.
    "Marketing.blocks.imagePending": "Not uploaded yet. It uploads automatically when you send, test, or preview.",
    // ensureBlocksImageUploaded() failed at one of the three transmission
    // points. A dedicated message per path, rather than one generic string,
    // so the owner knows exactly which action was cancelled.
    "Marketing.blocks.uploadFailedSend": "The layout image could not be uploaded, so the campaign was not sent.",
    "Marketing.blocks.uploadFailedTest": "The layout image could not be uploaded, so the test email was not sent.",
    "Marketing.blocks.uploadFailedPreview": "The layout image could not be uploaded, so the preview could not be generated.",
    "Marketing.tileSent": "Campaigns sent",

    // Marketing — audience segments (Marketing V2 Phase A)
    "Marketing.segment.label": "Audience",
    "Marketing.segment.range": "Appointment in a date range",
    "Marketing.segment.winback": "Win-back (lapsed clients)",
    "Marketing.segment.singleVisit": "Visited once",
    "Marketing.segment.newClients": "New clients",
    "Marketing.segment.loyal": "Loyal clients",
    "Marketing.segment.birthday": "Birthdays",
    "Marketing.segment.birthdayFrom": "From",
    "Marketing.segment.birthdayTo": "to",
    "Marketing.composer.descriptionBirthday": "Clients whose birthday falls within the chosen period. Only client files with a birthday are included.",
    "Marketing.history.birthdayAutomatic": "Birthday, automatic",
    "Marketing.history.birthdayRange": "Birthdays from {from} to {to}",
    "Marketing.automation.tab": "Automations",
    "Marketing.automation.title": "Automatic birthday email",
    "Marketing.automation.description": "Every morning at 9 a.m., Flowcut sends this email to clients whose birthday falls on that day, or in the number of days you choose. Each client receives it at most once a year.",
    "Marketing.automation.enabled": "Automatic sending enabled",
    "Marketing.automation.missingAddress": "This birthday email will not be sent while your salon’s address is empty. Canada’s anti-spam law requires a mailing address in every promotional email. Add it in Salon Information.",
    "Marketing.automation.timingLabel": "When to send",
    "Marketing.automation.timingSameDay": "On the day",
    "Marketing.automation.timingBefore": "A few days before",
    "Marketing.automation.daysBefore": "Number of days before the birthday",
    "Marketing.automation.daysInvalid": "Enter a number of days between 1 and 30.",
    "Marketing.automation.save": "Save",
    "Marketing.automation.saving": "Saving…",
    "Marketing.automation.saved": "Birthday email saved.",
    "Marketing.automation.saveError": "Saving failed. Try again or contact support.",
    "Marketing.automation.loadError": "The birthday email settings could not be loaded.",
    "Marketing.automation.retry": "Try again",
    "Marketing.automation.counter": "{total} clients have a birthday on file, including {soon} in the next 30 days.",
    "Marketing.automation.counterOne": "{total} client has a birthday on file, including {soon} in the next 30 days.",
    "Marketing.automation.counterEmpty": "No client has a birthday on file. Add it to the client file so this email can go out.",
    "Marketing.automation.subjectRequired": "A subject is required to enable automatic sending.",
    "Marketing.automation.bodyRequired": "A message is required to enable automatic sending.",
    "Marketing.automation.previewNeedsContent": "Fill in the subject and the message first.",
    "Marketing.automation.preview": "Preview",
    "Marketing.automation.sendTest": "Send me a test",
    "Marketing.segment.winbackDays": "Last visit more than (days):",
    "Marketing.segment.loyalMin": "Minimum number of visits:",
    "Marketing.segment.singleVisitHint": "Clients with exactly one past visit and no upcoming appointment.",
    "Marketing.cap.over": "Audience of {total}, over the maximum of {cap}. Narrow your segment.",
    "Marketing.audience.range": "Range: {start} → {end}",
    "Marketing.audience.newClients": "New clients: {start} → {end}",
    "Marketing.audience.winback": "Win-back · last visit over {days} days ago",
    "Marketing.audience.loyal": "Loyal clients · ≥ {count} visits",
  },
  fr: {

    // Module Marketing
    "Marketing.title": "Marketing",
    "Marketing.composer.title": "Nouvelle campagne courriel",
    "Marketing.composer.description": "Envoyez un courriel aux clients ayant un rendez-vous dans la plage de dates choisie.",
    "Marketing.composer.descriptionWinback": "Envoyez un courriel aux clients qui ne sont pas revenus depuis le nombre de jours choisi.",
    "Marketing.composer.descriptionSingleVisit": "Envoyez un courriel aux clients venus une seule fois et sans rendez-vous à venir.",
    "Marketing.composer.descriptionNewClients": "Envoyez un courriel aux clients dont la fiche a été créée dans la plage de dates choisie.",
    "Marketing.composer.descriptionLoyal": "Envoyez un courriel aux clients ayant atteint le nombre de visites choisi.",
    "Marketing.type.label": "Type de courriel",
    "Marketing.type.operational": "Opérationnel",
    "Marketing.type.marketing": "Promotionnel",
    "Marketing.type.operationalHelper": "Avis de service envoyé à tous les clients de la plage (ex. fermeture, changement d'horaire).",
    "Marketing.type.marketingHelper": "Promotion : exclut les clients désabonnés et ajoute un lien de désabonnement.",
    "Marketing.range.label": "Plage de dates",
    "Marketing.range.day": "Jour",
    "Marketing.range.week": "Semaine",
    "Marketing.range.month": "Mois",
    "Marketing.range.custom": "Personnalisée",
    "Marketing.range.start": "Date de début",
    "Marketing.range.end": "Date de fin",
    "Marketing.subject.label": "Sujet",
    "Marketing.subject.placeholder": "Sujet du courriel",
    "Marketing.body.label": "Message",
    "Marketing.body.placeholder": "Rédigez votre message…",
    "Marketing.charCount": "{count} / {max}",
    "Marketing.preview.loading": "Calcul des destinataires…",
    "Marketing.preview.count": "{count} clients seront contactés",
    "Marketing.preview.countOne": "{count} client sera contacté",
    "Marketing.preview.excluded": "({count} exclus car désabonnés)",
    "Marketing.preview.excludedOne": "({count} exclu car désabonné)",
    "Marketing.preview.unreachable": "Non joignables : {count}. Sans fiche client, aucun lien de désabonnement ne peut être fourni, ce que la loi exige pour un courriel promotionnel. Créez la fiche pour les inclure.",
    "Marketing.confirm.unreachable": " · {count} non joignables",
    "Marketing.confirm.unreachableOne": " · {count} non joignable",
    "Marketing.confirm.excluded": " · {count} désabonnés",
    "Marketing.confirm.excludedOne": " · {count} désabonné",
    "Marketing.preview.none": "Aucun client ne correspond à cette audience",
    "Marketing.preview.noneRange": "Aucun client dans cette plage",
    "Marketing.preview.error": "Impossible de compter les destinataires",
    "Marketing.preview.viewRecipients": "Voir les destinataires",
    "Marketing.preview.hideRecipients": "Masquer les destinataires",
    "Marketing.preview.viewUnreachable": "Voir les {count} personnes non joignables",
    "Marketing.preview.viewUnreachableOne": "Voir la personne non joignable",
    "Marketing.preview.hideUnreachable": "Masquer les personnes non joignables",
    "Marketing.preview.recipientsLoading": "Chargement des destinataires…",
    "Marketing.preview.recipientsError": "Impossible de charger les destinataires",
    "Marketing.preview.recipientsTruncated": "Les 1 000 premiers destinataires sont affichés, sur {total}.",
    "Marketing.preview.listTruncated": "Seuls les 1 000 premiers sont affichés.",
    "Marketing.send.when": "Quand",
    "Marketing.send.now": "Maintenant",
    "Marketing.send.schedule": "Programmer",
    "Marketing.send.scheduledFor": "Programmé pour",
    "Marketing.send.scheduledHelper": "L'heure d'envoi doit être dans le futur.",
    "Marketing.send.submit": "Envoyer",
    "Marketing.send.submitScheduled": "Programmer",
    "Marketing.send.sending": "Envoi…",
    "Marketing.send.test": "Envoyer un test à moi-même",
    "Marketing.send.testSending": "Envoi du test…",
    "Marketing.send.testHelper": "Le test sera envoyé à {email}",
    "Marketing.toast.created": "Campagne créée, {count} destinataires",
    "Marketing.toast.scheduled": "Campagne programmée, {count} destinataires",
    "Marketing.toast.noRecipients": "Aucun client dans cette plage",
    "Marketing.toast.testSent": "Courriel test envoyé",
    "Marketing.toast.createFailed": "Impossible de créer la campagne",
    "Marketing.toast.testFailed": "Impossible d'envoyer le courriel test",
    "Marketing.toast.missingAddress": "Ajoutez l’adresse de votre salon avant d’envoyer une campagne. La loi canadienne anti-pourriel exige une adresse postale dans chaque courriel promotionnel.",
    "Marketing.limit.remaining": "{used} / {max} campagnes ce mois-ci",
    "Marketing.limit.reached": "Limite mensuelle atteinte ({limit} campagnes/mois)",
    "Marketing.validation.subjectRequired": "Un sujet est requis",
    "Marketing.validation.bodyRequired": "Un message est requis",
    "Marketing.validation.rangeInvalid": "La date de début doit précéder la date de fin",
    "Marketing.validation.scheduleInPast": "L'heure programmée doit être dans le futur",
    "Marketing.validation.previewPending": "Le calcul des destinataires est en cours, réessayez dans un instant",
    "Marketing.history.title": "Historique des campagnes",
    "Marketing.history.empty": "Aucune campagne pour l'instant",
    "Marketing.history.loading": "Chargement des campagnes…",
    "Marketing.history.recipients": "{count} destinataires",
    "Marketing.history.results": "{sent} envoyés / {failed} échecs",
    "Marketing.history.scheduledFor": "Programmé pour {date}",
    "Marketing.history.range": "{start} → {end}",
    "Marketing.history.cancel": "Annuler",
    "Marketing.history.cancelConfirmAction": "Annuler la campagne",
    "Marketing.history.cancelKeep": "Garder la campagne",
    "Marketing.history.cancelling": "Annulation…",
    "Marketing.history.cancelConfirm": "Annuler cette campagne programmée ?",
    "Marketing.history.cancelConfirmDescription": "Elle ne sera pas envoyée. Cette action est irréversible.",
    "Marketing.history.cancelTitle": "Annuler la campagne",
    "Marketing.history.cancelSuccess": "Campagne annulée",
    "Marketing.history.cancelFailed": "Impossible d'annuler la campagne",
    "Marketing.status.draft": "Brouillon",
    "Marketing.status.scheduled": "Programmée",
    "Marketing.status.sending": "Envoi en cours",
    "Marketing.status.sent": "Envoyée",
    "Marketing.status.failed": "Échec",
    "Marketing.status.cancelled": "Annulée",
    "Marketing.tokens.legend": "Les champs seront remplacés par les infos de chaque client à l'envoi.",
    "Marketing.tokens.clientGroup": "Client",
    "Marketing.tokens.salonGroup": "Salon",
    "Marketing.tokens.nom_complet": "Nom complet",
    "Marketing.tokens.prenom": "Prénom",
    "Marketing.tokens.courriel_client": "Courriel",
    "Marketing.tokens.telephone_client": "Téléphone",
    "Marketing.tokens.salon": "Nom du salon",
    "Marketing.tokens.adresse": "Adresse",
    "Marketing.tokens.telephone_salon": "Téléphone",
    "Marketing.tokens.courriel_salon": "Courriel",
    "Marketing.tokens.site_web": "Site web",
    "Marketing.tokens.tooLong": "Ce champ est limité à {max} caractères, le jeton n'y tient pas.",
    "Marketing.draft.reset": "Réinitialiser",
    "Marketing.draft.restored": "Brouillon restauré",
    "Marketing.resetConfirmTitle": "Réinitialiser la campagne ?",
    "Marketing.resetConfirmBody": "Le sujet, le message et tous les réglages reviendront à leurs valeurs par défaut. Cette action est irréversible.",
    "Marketing.resetConfirmAction": "Réinitialiser",
    "Marketing.previewEmail.button": "Aperçu",
    "Marketing.previewEmail.loading": "Génération de l'aperçu…",
    "Marketing.previewEmail.titlePrefix": "Aperçu",
    "Marketing.previewEmail.subjectLabel": "Sujet :",
    "Marketing.previewEmail.error": "Impossible de générer l'aperçu",
    "Marketing.confirm.title": "Envoyer cette campagne ?",
    "Marketing.confirm.body": "{count} destinataires · {type} · {range} · {when}",
    "Marketing.confirm.bodyOne": "{count} destinataire · {type} · {range} · {when}",
    "Marketing.confirm.typeOperational": "Opérationnel",
    "Marketing.confirm.typeMarketing": "Promotionnel",
    "Marketing.confirm.whenNow": "envoi immédiat",
    "Marketing.confirm.whenScheduled": "programmé pour {date}",
    "Marketing.confirm.confirm": "Envoyer",
    "Marketing.confirm.confirmScheduled": "Programmer",
    "Marketing.confirm.cancel": "Annuler",
    "Marketing.history.duplicate": "Dupliquer",

    // Marketing — en-tête unifié
    "Marketing.headerSubtitle": "Campagnes courriel vers vos clients",
    "Marketing.tileCampaignsThisMonth": "Campagnes ce mois",
    "Marketing.tileRecipients": "Destinataires ciblés",
    "Marketing.tileScheduled": "Planifiées",
    "Marketing.tileLastSend": "Dernier envoi",
    "Marketing.tileNeverSent": "Aucun",
    "Marketing.tabCompose": "Composer",
    "Marketing.tabHistory": "Historique",
    "Marketing.tabBrand": "Image de marque",
    "Marketing.brand.title": "Votre image de marque",
    "Marketing.brand.description": "À configurer une seule fois. Tous vos courriels de campagne l'utiliseront automatiquement.",
    "Marketing.brand.logo": "Logo",
    "Marketing.brand.logoHelper": "Affiché en haut de chaque courriel de campagne. Par défaut : le logo de votre salon.",
    "Marketing.brand.banner": "Bannière",
    "Marketing.brand.bannerHelper": "Image large optionnelle, au-dessus du logo. Activable campagne par campagne.",
    "Marketing.brand.upload": "Téléverser une image",
    "Marketing.brand.remove": "Retirer",
    "Marketing.brand.usingSalonLogo": "Logo du salon utilisé",
    "Marketing.brand.accent": "Couleur d'accent",
    "Marketing.brand.accentAuto": "Automatique",
    "Marketing.brand.accentHelper": "Utilisée pour les titres et le bouton d'action.",
    "Marketing.brand.ctaLabel": "Libellé du bouton",
    "Marketing.brand.ctaLabelPlaceholder": "Prendre rendez-vous",
    "Marketing.brand.ctaUrl": "Lien du bouton",
    "Marketing.brand.ctaUrlInvalid": "Entrez un lien commençant par http:// ou https://.",
    "Marketing.brand.useBookingLink": "Utiliser ma page de réservation",
    "Marketing.brand.instagram": "Instagram",
    "Marketing.brand.facebook": "Facebook",
    "Marketing.brand.signature": "Signature",
    "Marketing.brand.signaturePlaceholder": "L'équipe de …",
    "Marketing.brand.contactNote": "Votre adresse, téléphone et site web proviennent d'« Informations du salon » et apparaissent au pied du courriel.",
    "Marketing.brand.save": "Enregistrer l'image de marque",
    "Marketing.brand.saved": "Image de marque enregistrée",
    "Marketing.brand.oldImageNotRemoved": "Vos images sont enregistrées, mais un ancien fichier n'a pas pu être supprimé.",
    "Marketing.brand.saveFailed": "Impossible d'enregistrer l'image de marque",
    "Marketing.brand.loadFailed": "Impossible de charger l'image de marque. Réessayez plus tard. Enregistrer maintenant effacerait ce qui est déjà configuré.",
    "Marketing.brand.uploadFailed": "Impossible de téléverser l'image",
    "Marketing.brand.imageTooLarge": "L'image doit faire 5 Mo ou moins",
    "Marketing.brand.imageInvalidType": "Utilisez une image JPEG, PNG ou WebP",
    "Marketing.brand.unsavedImage": "Aperçu non enregistré. Cliquez sur Enregistrer pour le garder.",
    "Marketing.blocks.title": "Mise en page (optionnel)",
    "Marketing.blocks.heading": "Titre",
    "Marketing.blocks.headingPlaceholder": "Promo d'été",
    "Marketing.blocks.image": "Image",
    "Marketing.blocks.imageUpload": "Téléverser une image",
    "Marketing.blocks.imageRemove": "Retirer l'image",
    "Marketing.blocks.showBanner": "Afficher ma bannière",
    "Marketing.blocks.showCta": "Afficher un bouton d'action",
    "Marketing.blocks.ctaLabelOverride": "Libellé du bouton (remplace votre image de marque)",
    "Marketing.blocks.ctaUrlOverride": "Lien du bouton (remplace votre image de marque)",
    "Marketing.blocks.brandHint": "Un champ laissé vide reprend votre image de marque.",
    "Marketing.blocks.bannerMissing": "Aucune bannière dans votre image de marque, rien ne s'affichera. Ajoutez-la dans l'onglet Image de marque.",
    "Marketing.blocks.ctaMissing": "Aucun lien de bouton, ni ici ni dans votre image de marque, donc aucun bouton ne s'affichera.",
    "Marketing.blocks.imagePending": "Pas encore téléversée. Le téléversement se fait automatiquement à l'envoi, au test ou à l'aperçu.",
    "Marketing.blocks.uploadFailedSend": "Impossible de téléverser l'image de mise en page, l'envoi a été annulé.",
    "Marketing.blocks.uploadFailedTest": "Impossible de téléverser l'image de mise en page, le courriel de test a été annulé.",
    "Marketing.blocks.uploadFailedPreview": "Impossible de téléverser l'image de mise en page, l'aperçu a été annulé.",
    "Marketing.tileSent": "Campagnes envoyées",

    // Marketing — segments d'audience (Marketing V2 Phase A)
    "Marketing.segment.label": "Audience",
    "Marketing.segment.range": "Rendez-vous dans une plage",
    "Marketing.segment.winback": "Reconquête (pas revenu·e)",
    "Marketing.segment.singleVisit": "Venu·e une seule fois",
    "Marketing.segment.newClients": "Nouveaux clients",
    "Marketing.segment.loyal": "Clients fidèles",
    "Marketing.segment.birthday": "Anniversaires",
    "Marketing.segment.birthdayFrom": "Du",
    "Marketing.segment.birthdayTo": "au",
    "Marketing.composer.descriptionBirthday": "Clients dont la date de fête tombe dans la période choisie. Seules les fiches qui portent un anniversaire sont incluses.",
    "Marketing.history.birthdayAutomatic": "Anniversaire, automatique",
    "Marketing.history.birthdayRange": "Anniversaires du {from} au {to}",
    "Marketing.automation.tab": "Automatisations",
    "Marketing.automation.title": "Courriel d'anniversaire automatique",
    "Marketing.automation.description": "Chaque matin à 9 h, Flowcut envoie ce courriel aux clients dont la fête tombe ce jour-là, ou dans le nombre de jours choisi. Chaque client le reçoit au plus une fois par année.",
    "Marketing.automation.enabled": "Envoi automatique activé",
    "Marketing.automation.missingAddress": "Ce courriel d’anniversaire ne partira pas tant que l’adresse de votre salon est vide. La loi canadienne anti-pourriel exige une adresse postale dans chaque courriel promotionnel. Ajoutez-la dans Informations du salon.",
    "Marketing.automation.timingLabel": "Moment de l'envoi",
    "Marketing.automation.timingSameDay": "Le jour même",
    "Marketing.automation.timingBefore": "Quelques jours avant",
    "Marketing.automation.daysBefore": "Nombre de jours avant la fête",
    "Marketing.automation.daysInvalid": "Indiquez un nombre de jours entre 1 et 30.",
    "Marketing.automation.save": "Enregistrer",
    "Marketing.automation.saving": "Enregistrement…",
    "Marketing.automation.saved": "Courriel d'anniversaire enregistré.",
    "Marketing.automation.saveError": "L'enregistrement a échoué. Réessayez ou contactez le soutien.",
    "Marketing.automation.loadError": "Impossible de charger les réglages du courriel d'anniversaire.",
    "Marketing.automation.retry": "Réessayer",
    "Marketing.automation.counter": "{total} clients ont une date de fête enregistrée, dont {soon} dans les 30 prochains jours.",
    "Marketing.automation.counterOne": "{total} client a une date de fête enregistrée, dont {soon} dans les 30 prochains jours.",
    "Marketing.automation.counterEmpty": "Aucun client n'a de date de fête enregistrée. Ajoutez-la dans la fiche client pour que ce courriel parte.",
    "Marketing.automation.subjectRequired": "Le sujet est requis pour activer l'envoi automatique.",
    "Marketing.automation.bodyRequired": "Le contenu est requis pour activer l'envoi automatique.",
    "Marketing.automation.previewNeedsContent": "Remplissez d'abord le sujet et le contenu.",
    "Marketing.automation.preview": "Aperçu",
    "Marketing.automation.sendTest": "M'envoyer un test",
    "Marketing.segment.winbackDays": "Dernière visite il y a plus de (jours) :",
    "Marketing.segment.loyalMin": "Nombre minimum de visites :",
    "Marketing.segment.singleVisitHint": "Clients avec exactement une visite passée et aucun rendez-vous à venir.",
    "Marketing.cap.over": "Audience de {total}, dépasse le maximum de {cap}. Resserrez votre segment.",
    "Marketing.audience.range": "Plage : {start} → {end}",
    "Marketing.audience.newClients": "Nouveaux clients : {start} → {end}",
    "Marketing.audience.winback": "Reconquête · dernière visite il y a plus de {days} j",
    "Marketing.audience.loyal": "Clients fidèles · ≥ {count} visites",
  },
};
